import { LowerCasePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  computed,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Params, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { InputTextModule } from 'primeng/inputtext';
import { Observable, of } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { FoodSafetyList } from '../../kuba/foodsafety/models/foodsafety-list';
import { FoodSafetyNavigationService } from '../../kuba/foodsafety/services/foodsafety-navigation.service';
import { ManualView } from '../../kuba/manuals/models';
import { ManualNavigationService } from '../../kuba/manuals/services/manual-navigation.service';
import { SubNavigation, SubNavigationLink, zoneTabLinks } from './sub-navigation';

/** Above this many records the panel offers a search field. */
const RECORD_SEARCH_THRESHOLD = 7;

interface PanelRow {
  key: string;
  /** Translation key, or plain text when `translate` is false. */
  label: string;
  translate: boolean;
  commands: any[];
  queryParams?: Params;
  /** PrimeIcons class. */
  icon?: string;
}

interface PanelState {
  loading: boolean;
  rows: PanelRow[];
  /** The rows are records, such as manuals or zones, rather than tabs. */
  records: boolean;
  /** Zone name, when the rows are the tabs of the only zone. */
  caption?: string;
}

/**
 * The hover panel beside the left navigation: a module's tabs, the manual list
 * for Manuals, or the zones for Food Safety, each opened in one click.
 */
@Component({
  selector: 'sub-navigation-panel',
  standalone: true,
  templateUrl: './sub-navigation-panel.component.html',
  styleUrls: ['./sub-navigation-panel.component.scss'],
  imports: [
    InputTextModule,
    LowerCasePipe,
    RouterLink,
    RouterLinkActive,
    TranslatePipe
  ]
})
export class SubNavigationPanelComponent {
  /** The top-level feature the panel belongs to. */
  readonly feature = input.required<any>();
  readonly menu = input.required<SubNavigation>();
  readonly urlBase = input.required<string>();
  /** Translation key of the module name. */
  readonly label = input.required<string>();
  /** A link in the panel was chosen. */
  readonly navigated = output<void>();
  /** The user asked to leave the panel with the keyboard. */
  readonly dismissed = output<void>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly translate = inject(TranslateService);
  private readonly manualNavigation = inject(ManualNavigationService);
  private readonly foodSafetyNavigation = inject(FoodSafetyNavigationService);

  readonly query = signal('');
  readonly state = toSignal(
    toObservable(this.menu).pipe(switchMap(menu => this.load(menu))),
    { initialValue: { loading: true, rows: [], records: false } as PanelState }
  );
  readonly emptyKey = computed(() =>
    this.menu().kind === 'manuals' ? 'NO_MANUALS_ASSIGNED' : 'NO_RECORDS_FOUND'
  );
  readonly searchable = computed(
    () =>
      this.state().records &&
      this.state().rows.length > RECORD_SEARCH_THRESHOLD
  );
  readonly visibleRows = computed(() => {
    const rows = this.state().rows;
    const query = this.query().trim().toLowerCase();
    if (!this.searchable() || !query) {
      return rows;
    }
    return rows.filter(row =>
      `${row.translate ? this.translate.instant(row.label) : row.label}`
        .toLowerCase()
        .includes(query)
    );
  });

  onKeydown(event: KeyboardEvent): void {
    const focusables = this.focusables();
    const index = focusables.indexOf(document.activeElement as HTMLElement);
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.dismissed.emit();
        break;
      case 'ArrowLeft':
        if (!(event.target instanceof HTMLInputElement)) {
          event.preventDefault();
          this.dismissed.emit();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        focusables[Math.min(index + 1, focusables.length - 1)]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusables[Math.max(index - 1, 0)]?.focus();
        break;
    }
  }

  private load(menu: SubNavigation): Observable<PanelState> {
    switch (menu.kind) {
      case 'tabs':
        return of({ loading: false, records: false, rows: menu.links.map(tabRow) });
      case 'manuals':
        return this.loadRecords(
          this.manualNavigation.manuals().pipe(
            map((manuals): PanelState => ({
              loading: false,
              records: true,
              rows: manuals.map(manual => this.manualRow(manual))
            }))
          )
        );
      case 'zones':
        return this.loadRecords(
          this.foodSafetyNavigation.zones().pipe(
            map((zones): PanelState =>
              zones.length === 1
                ? {
                    loading: false,
                    records: false,
                    caption: zones[0].ZoneName,
                    rows: zoneTabLinks(menu, zones[0].Id).map(tabRow)
                  }
                : {
                    loading: false,
                    records: true,
                    rows: zones.map(zone => zoneRow(menu.modulePath, zone))
                  }
            )
          )
        );
    }
  }

  private loadRecords(records: Observable<PanelState>): Observable<PanelState> {
    return records.pipe(
      catchError(() => of<PanelState>({ loading: false, records: true, rows: [] })),
      startWith<PanelState>({ loading: true, records: true, rows: [] })
    );
  }

  /** Opens a manual, as the Manuals page links it. */
  private manualRow(manual: ManualView): PanelRow {
    return {
      key: `manual-${manual.AppManualId}`,
      label: manual.Title,
      translate: true,
      icon: 'pi-book',
      commands: [
        `${this.urlBase()}/manual/view`,
        manual.AppManualId,
        manual.Title,
        manual.PortalId,
        manual.BusinessId
      ]
    };
  }

  private focusables(): HTMLElement[] {
    return Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement>('a[href], input')
    );
  }
}

function tabRow(link: SubNavigationLink): PanelRow {
  return {
    key: `${link.commands[0]}`,
    label: link.translationKey,
    translate: true,
    commands: link.commands
  };
}

/** Opens a zone, as the zone list page links it. */
function zoneRow(modulePath: string, zone: FoodSafetyList): PanelRow {
  return {
    key: `zone-${zone.Id}`,
    label: zone.ZoneName,
    translate: false,
    icon: 'pi-map-marker',
    commands: [`${modulePath}/details`, zone.Id],
    queryParams: { Title: zone.ZoneName }
  };
}
